import { SVC_A, SVC_B, SVC_C, SVC_C_RT, SVC_D } from './base'
import moment from 'moment'

// import queryString from "query-string";

/**
 * Fetches faces from the server.
 *
 * @export
 * @param {String} {domainUUID} A domain UUID to filter faces for.
 * @returns
 */
export function loadFacesByDomainUUID ({ domainUUID }) {
  let params = {}

  if (domainUUID) {
    params.domain_uuid = domainUUID
  }

  return SVC_C.post('/api/v5/faces', params)
}

/*
Sample response:
{
  "ok": true,
  "matches": [
    {
      "frontend_face_uuid": "6ccea727-0673-4315-8d1d-2d5d5a342d67",
      "person_name": "Silvio Ortiz",
      "image_uri": "https://s3.amazonaws.com/owal-clips/faces/Silvio Ortiz.JPG",
      "diff": 0.5889765314668264
    },
    ...
  ]
}
*/

export function faceconfirm (detectionId, isFace, domainUUID) {
  const params = {
    detectionId,
    isFace,
    domain_uuid: domainUUID,
  }
  return SVC_A.post('/api/faces/v1/detection-face-confirm', params)
}

export function facematchconfirm (detectionId, frontendFaceUUID, isCorrect, domainUUID, faceMatchId = undefined) {
  let params = {
    frontendFaceUUID,
    isCorrect,
    domain_uuid: domainUUID,
    faceMatchId,
  }

  if (Array.isArray(detectionId)) {
    params.matches = detectionId
  } else {
    params.matches = [detectionId]
  }
  return SVC_A.post('/api/faces/v1/detection-face-match', params)
}

export function facesdetected2 ({
  frontendFaceUUID = undefined,
  refreshToken,
  minimumFaceHeight = 0,
  domainUUID,
  cameraUUID,
  faceUUIDs,
  confidence,
  confidencePredicate,
  cameraUUIDs,
  limit = 20,
  shapes = false,
  clahe = false,
  modelKey = undefined,
}) {
  const params = {
    min_face_height: minimumFaceHeight,
    limit,
  }

  if (faceUUIDs && faceUUIDs.length > 0) {
    params.face_uuids = faceUUIDs
  } else if (frontendFaceUUID) {
    params.face_uuids = [frontendFaceUUID]
  }

  if (refreshToken) {
    params.refresh_token = refreshToken
  }

  if (minimumFaceHeight) {
    params.min_face_height = minimumFaceHeight
  }

  if (domainUUID) {
    params.domain_uuid = domainUUID
  }

  if (cameraUUIDs) {
    params.camera_uuids = cameraUUIDs
  } else if (cameraUUID) {
    params.camera_uuids = [cameraUUID]
  }

  if (confidence && confidencePredicate) {
    params.confidence_operator = confidencePredicate
    params.confidence = confidence
  }

  if (modelKey) {
    params.model_key = modelKey
  }

  return SVC_C_RT.post('/api/v5/facesdetected2', params)
}

// NOTE: offset and batchsize are both disabled in the API endpoint.
export function facesconfirmed (domainUUID = null, offset = 0, batchSize = 50, frontendFaceUUID, modelKey = undefined, incorrect = false) {
  return SVC_A.post('/api/v4/faces/confirmed', {
    domain_uuid: domainUUID,
    offset,
    limit: batchSize,
    frontend_face_uuid: frontendFaceUUID,
    model_key: modelKey,
    incorrect,
  })
}

/**
 * Fetches the last seen for all faces.
 *
 * @export
 * @returns A promise for the last seen list.
 */
export function updateLastSeen (domainUUID) {
  return SVC_C.get(`/api/v6/faces/last_seen?domain_uuid=${domainUUID}`)
}

/**
 * Fetches the detection history for detections for all faces, or a given face.
 *
 * @export
 * @param {any} frontendFaceUUID A frontend face UUID to fetch the data.
 * @returns A promise for the history data.
 */
export function updateHistory (frontendFaceUUID) {
  /* eslint-disable camelcase */
  // return SVC_C.get(`/api/v6/faces/matched/${frontendFaceUUID}?min_match_confidence_level=1`)
  return SVC_A.post('/api/faces/v1/search_history', {
    frontend_face_uuid: frontendFaceUUID,
    min_match_confidence_level: 1,
  }).then((response) => {
    if (response.data.ok) {
      return response.data.results
    } else {
      throw new Error('Failed to fetch search history')
    }
  }).then((search_history_results) => {
    const faces_matched = search_history_results.map((result) => {
      result.extra_metadata = undefined
      result.frame_width = undefined
      result.frame_height = undefined
      result.face_width = undefined
      result.face_top = undefined
      result.face_left = undefined
      const face_confidence = result.detector_confidence
      return {
        ...result,
        face_confidence,
        captured_at: result.captured_at.replace(' ', 'T') + 'Z',
        frontend_face_uuid: frontendFaceUUID,
      }
    })
    return { data: { faces_matched } }
  }).catch((error) => {
    console.error('Error fetching search history:', error)
    throw error
  })
}

/**
 * Runs the faces training model.
 *
 * @export
 * @returns A promise to run the training model
 */
export function trainModel (domainUUID) {
  return SVC_B.post(`/api/v1/model/train/${domainUUID}`)
}

export function listModelsForDomain (domainUUID) {
  return SVC_B.get(`/api/v1/model/${domainUUID}`)
}

export function getDetailsForModel (domainUUID, modelId) {
  return SVC_B.get(`/api/v1/model/${domainUUID}/${modelId}`)
}

export function getCurrentModelLabel (domainUUID) {
  return SVC_C_RT.get(`/api/v6/faces/models/${domainUUID}/label`)
}

export function setNewFacesModel (domainUUID, modelId) {
  return SVC_C_RT.post(`/api/v6/faces/models/${domainUUID}/${modelId}/serve`)
}

/**
 *  Converts a UUID into a credential image URL.
 *
 * @param {string} credentialUUID The UUID for the credential in question.
 *
 * @returns A string containing the full URL to a credential image.
 */
export function imageForCredentialUUID (credentialUUID, bustCache = false) {
  const cacheBuster = bustCache ? `?${cacheBusterString()}` : ''
  return `${SVC_D.getBaseUri()}/api/v6/cards/${credentialUUID}/image${cacheBuster}`
}

/**
 *  Converts a hashids encoded detection ID into an image URL.
 *
 * @param {string} detectionID The identity the person to display.
 *
 * @returns A string containing the full URL to a detection image.
 */
export function imageForDetectionID (detectionID, options) {
  const { fullFrame = false, bustCache = false } = options || {}
  const queryParams = []
  if (fullFrame) {
    queryParams.push('cropped=0')
  }
  if (bustCache) {
    queryParams.push(cacheBusterString())
  }
  const queryString = queryParams ? `?${queryParams.join('&')}` : ''
  return `${SVC_D.getBaseUri()}/api/v7/faces/detected/${detectionID}/image${queryString}`
}

/**
 *  Converts an identity ID into an image URL.
 *
 * @param {string} identityId The identity the person to display.
 *
 * @returns A string containing the full URL to a credential image.
 *
 * cacheBuster: changing someone's profile picture in the OWAL Faces Profile is not immediately
 * due to browser caching, which is overridden by addeed parameter (and does nothing else)
 */
export function urlForIdentityId (identityId, bustCache = false) {
  const cacheBuster = bustCache ? `?${cacheBusterString()}` : ''
  return `${SVC_D.getBaseUri()}/api/v7/faces/identities/${identityId}/image${cacheBuster}`
}

/**
 *  Returns an image URL from a frontend face UUID.
 *
 * @param {string} frontendFaceUUID The frontend face uuid to match.
 *
 * @returns A string containing the full URL to a frontend face image.
 */
export function urlForFrontendFaceId (frontendFaceUUID, bustCache = false) {
  const cacheBuster = bustCache ? `?${cacheBusterString()}` : ''
  return `${SVC_D.getBaseUri()}/api/v7/faces/frontend/${frontendFaceUUID}/image${cacheBuster}`
}

/**
 *  Returns the image data from S3 for and id.
 *
 * @param {string} identityId The identity the person to display.
 *
 * @returns A string containing the full URL to an identity image.
 */
export function imageForIdentityId (identityId, params) {
  return SVC_D.getImage(`/api/v7/faces/identities/${identityId}/image`, params)
}

export function fetchMatchImages (options) {
  const { domainUUID, searchStart, searchEnd, detectionId, cameraUUID, image } = options
  let queryString = `?domain_uuid=${domainUUID}`
  queryString += `${searchStart ? `&search_start_ts=${moment.utc(searchStart).format()}` : ''}`
  queryString += `${searchEnd ? `&search_end_ts=${moment.utc(searchEnd).format()}` : ''}`
  queryString += `${detectionId ? `&detection_id=${detectionId}` : ''}`
  queryString += `${cameraUUID ? `&camera_uuids=${cameraUUID}` : ''}`
  queryString += `&returning_primary_key=detection_id`

  let formDataToUpload = new FormData()

  if (image) {
    formDataToUpload.append('img', image, 'img.jpeg')
  }
  const params = image ? formDataToUpload : {}

  return SVC_A.post(`/api/faces/v1/tag_search${queryString}`, params)
}

// update is_locked for an array of face_match_uuids where { face_match_uuid, is_locked }

export function updateLockedTrainingFor (faceMatchPairs, domainUUID) {
  return SVC_A.post('/api/v4/faces/lock_tag', { tag_ids: faceMatchPairs, domain_uuid: domainUUID })
}

function cacheBusterString () {
  const cacheBuster = Math.round(new Date().getTime() / 1000)
  return `cacheBuster=${cacheBuster}`
}

export function loadTracks({ domainUUID, since, ...params }) {
  return SVC_A.get(`/api/faces/v1/tracks?domain_uuid=${domainUUID}&since=${since}`)
}

export function addTrack({ domainUUID, ...params }) {
  return SVC_A.post(`/api/faces/v1/tracks?domain_uuid=${domainUUID}`)
}
export function updateTracks({ domainUUID, payload, originalTrackUUID, ...params }) {
  return SVC_A.put(`/api/faces/v1/tracks?domain_uuid=${domainUUID}`, { payload, originalTrackUUID })
}

export function verifyTrack({ domainUUID, trackUUID, ...params }) {
  return SVC_A.post(`/api/faces/v1/tracks/${trackUUID}/approve?domain_uuid=${domainUUID}`)
}

export function confirmTracks({ domainUUID, payload, ...params }) {
  return SVC_A.put(`/api/faces/v1/tracks?domain_uuid=${domainUUID}`, { payload })
}

export function loadTODForTrack(domainUUID, trackUUID, params) {
  return SVC_A.get(`/api/faces/v1/tracks/${trackUUID}?domain_uuid=${domainUUID}`, params)
}

export function loadFacesStats (domainUUID, params) {
  return SVC_A.get(`/api/events/v1/faces?domain_uuid=${domainUUID}`, params)
}

export function loadFacesStatsByFaceUUID (domainUUID, faceUUID, params) {
  return SVC_A.get(`/api/faces/v1/stats/${faceUUID}?domain_uuid=${domainUUID}`, params)
}
export function loadHeadPoseDataByFaceUUID (domainUUID, faceUUID, all, cameraUUID, params) {
  const cameraUUIDqueryParam = cameraUUID ? `&camera_uuid=${cameraUUID}` : ''
  const referenceParam = all ? `&all=${all}` : ``
  return SVC_A.get(`/api/faces/v1/headpose/${faceUUID}?domain_uuid=${domainUUID}${cameraUUIDqueryParam}${referenceParam}`, params)
}

export function loadAmbulatorySpeedByFaceUUID (domainUUID, faceUUID, params) {
  return SVC_A.get(`/api/faces/v1/ambulatory_speed/${faceUUID}?domain_uuid=${domainUUID}`, params)
}
export function loadMaskStats (domainUUID, days, params) {
  return SVC_A.get(`/api/events/v1/masks?domain_uuid=${domainUUID}&days=${days}`)
}

export function loadReports(domainUUID) {
  return SVC_A.get('/api/faces/v1/mask_reports')
}

// TODO add domain uuid?
export function sendReportApproval(reportId) {
  return SVC_A.put(`/api/faces/v1/mask_reports/${reportId}`)
}

export function searchMatchedFaces({
  domainUUID,
  startTime,
  endTime,
  cameraUUID, // Keep as optional parameter
  limit = 20,
  offset = 0,
}) {
  const params = {
    domain_uuid: domainUUID,
    startTime,
    endTime,
    limit,
    offset,
    token: true,
  }

  // Only add camera_uuid if it's provided
  if (cameraUUID) {
    params.camera_uuid = cameraUUID
  }

  return SVC_A.post('/api/faces/v1/search_matched', params)
}

/**
 * Loads the faces-running cameras for a domain along with their current
 * minimum-face-height overrides (both the matching/storing threshold and the alerting threshold).
 *
 * @param {String} domainUUID The domain to load camera settings for.
 * @returns Axios promise resolving to
 *   { ok, defaultMinFaceHeight, defaultMinFaceHeightForAlerting,
 *     cameras: [{ camera_uuid, description, min_face_height, min_face_height_for_alerting }] }
 */
export function loadMinFaceHeightSettings (domainUUID) {
  return SVC_A.get('/api/faces/v1/min-face-height', `?domain_uuid=${domainUUID}`)
}

/**
 * Saves per-camera minimum-face-height overrides for a domain.
 *
 * @param {String} domainUUID The domain the cameras belong to.
 * @param {Array} overrides [{ camera_uuid, min_face_height, min_face_height_for_alerting }] where
 *   each threshold is a positive integer to set an override, or null to clear it.
 * @returns Axios promise resolving to { ok }
 */
export function saveMinFaceHeightSettings (domainUUID, overrides) {
  return SVC_A.post('/api/faces/v1/min-face-height', { domain_uuid: domainUUID, overrides })
}
